import { useSFetch } from '../hooks/sfetch';
import { createSignal, onMount, Show } from 'solid-js';
import { For } from 'solid-js';
import { useRoleParser } from '../hooks/permissions';
import Text from '../components/Text';
import StateDisplay from '../components/StateDisplay';
import { showConfirm } from '../components/Dialog';
import { User } from '../components/UserContextProvider';
import { useNavigate } from '@solidjs/router';
import { useToast } from '../components/MessageBox';
import { useTranslation } from '../hooks/useTranslation';
import { formatDateTime } from '../services/dateFormat';

export interface Grant {
    id: string;
    userId: string;
    botId: string;
    createdAt: string;
    level: number;
}

export default function MyGrants() {

    const roles = ["Read", "Admin"];

    const navigate = useNavigate();
    const {parseRoles, roles: _} = useRoleParser();
    const sfetch = useSFetch();
    const toast = useToast();
    const { t } = useTranslation();
    const [grants, setGrants] = createSignal<Grant[]>([]);
    const [allUsers, setAllUsers] = createSignal<User[]>([]);
    const [loading, setLoading] = createSignal(true);

    const [grantName, setGrantName] = createSignal<string>("");

    onMount(async () => {
        const [grantsRes, usersRes] = await Promise.all([
            sfetch('/api/GrantedUsers/GetGrantedAccounts'),
            sfetch('/api/User/Users')
        ]);
        if (grantsRes.ok) setGrants(grantsRes.value ?? []);
        if (usersRes.ok) setAllUsers(usersRes.value ?? []);
        setLoading(false);
    });

    function submitNewCommand(e: Event) {
        e.preventDefault();

        sfetch('/api/GrantedUsers/GrantAccount/' + grantName(), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            }
        }).then(async data => {
            if (data.status == 200) {
                toast?.info(t('GRANT_CREATED'));
                const res = await sfetch('/api/GrantedUsers/GetGrantedAccounts');
                if (res.ok) setGrants(res.value ?? []);
                setGrantName("");
            }
            else {
                toast?.error(t('GRANT_CREATE_FAILED'));
            }
        });
    }

    async function deleteGrant(id: string) {
        if (!await showConfirm(t('GRANT_REVOKE_CONFIRM'), true)) return;
        sfetch('/api/GrantedUsers/RevokeAccount/' + id, {
            method: 'POST'
        }).then(async data => {
            if (data.status == 200) {
                toast?.info(t('GRANT_REVOKED'));
                const res = await sfetch('/api/GrantedUsers/GetGrantedAccounts');
                if (res.ok) setGrants(res.value ?? []);
            }
            else {
                toast?.error(t('GRANT_REVOKE_FAILED'));
            }
        });
    }

    return (
        <div class='dashboard-page'>
            <h1><Text val='MYGRANTS'></Text></h1>
            <form onSubmit={submitNewCommand} class='fcard h' style={{ "align-items": "center" }}>
                <input
                    type="text"
                    class="search-input"
                    placeholder={t('USERNAME')}
                    value={grantName()}
                    onInput={(e) => setGrantName((e.target as HTMLInputElement).value)}
                />
                <button aria-label="Add grant"><i class="fa-solid fa-plus" aria-hidden="true"></i></button>
            </form>
            <Show when={loading()}>
                <StateDisplay loading message="LOADING" />
            </Show>
            <Show when={!loading() && grants().length === 0}>
                <StateDisplay empty icon="fa-solid fa-share-nodes" message="GRANTS_EMPTY" helpText="GRANTS_EMPTY_HELP" />
            </Show>
            <Show when={grants().length > 0}>
                <table class="sessiontable">
                    <thead>
                        <tr>
                            <th><Text val="USERNAME"></Text></th>
                            <th><Text val="LEVEL"></Text></th>
                            <th><Text val="CREATEDAT"></Text></th>
                            <th><i class="fa-solid fa-pen-to-square"></i></th>
                            <th><i class="fa-solid fa-trash"></i></th>
                        </tr>
                    </thead>
                    <tbody>
                        <For each={grants()}>
                            {(command) => (
                                <tr>
                                    <td>{allUsers().filter((val) => val.id == command.userId)[0]?.username ?? "-"}</td>
                                    <td>{parseRoles(command.level, roles).map((val, i) =>
                                    <span class='h'>{roles[i]} {val ? <i class="fa-solid fa-check"></i> : <i class="fa-solid fa-xmark"></i>}</span>)}</td>
                                    <td>{formatDateTime(command.createdAt)}</td>
                                    <td><button onClick={() => navigate("/grant/" + command.id)}><i class="fa-solid fa-pen-to-square"></i></button></td>
                                    <td><button onClick={() => deleteGrant(command.id)} class='danger'><i class="fa-solid fa-trash"></i></button></td>
                                </tr>
                            )}
                        </For>
                    </tbody>
                </table>
            </Show>
        </div>
    )
}
